
import { Icon } from '@/components/ui/Icon'
import { cx } from '@/lib/ui'

/**
 * A collapsed side panel: a thin frosted strip that keeps the panel's place
 * (and its name) on screen, so hiding the library or the details never
 * leaves people wondering where it went. One click anywhere on it brings the
 * panel back.
 */
export function PanelRail({
  side,
  label,
  onExpand,
  attention = false,
  shortcut,
  className,
}: {
  side: 'left' | 'right'
  label: string
  onExpand: () => void
  /** A dot beside the label: there is something to see in here. */
  attention?: boolean
  /** Key that also opens it, shown in the tooltip. */
  shortcut?: string
  /** Breakpoint gating from the parent, mirroring the panel it stands in for. */
  className?: string
}) {
  const verb = `Show ${label.toLowerCase()}`
  const title = attention
    ? `${verb} — something is selected${shortcut ? ` (${shortcut})` : ''}`
    : `${verb}${shortcut ? ` (${shortcut})` : ''}`

  return (
    <aside className={cx('glass w-11 shrink-0 flex-col rounded-[var(--radius-panel)]', className)}>
      <button
        type="button"
        onClick={onExpand}
        aria-label={title}
        aria-expanded={false}
        title={title}
        className="flex min-h-0 flex-1 flex-col items-center gap-3 rounded-[var(--radius-panel)] py-2.5 text-muted transition-colors hover:bg-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {/* The chevron points the way the panel will grow. */}
        <Icon name={side === 'left' ? 'chevronRight' : 'chevronLeft'} className="h-4 w-4 shrink-0" />
        <span className="flex items-center gap-2 [writing-mode:vertical-rl] rotate-180">
          {attention && (
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-warning" />
          )}
          <span className="text-[11px] font-medium uppercase tracking-[0.08em]">{label}</span>
        </span>
      </button>
    </aside>
  )
}
